import React, { useState, useEffect } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import DoneIcon from '@mui/icons-material/Done'
import BlockIcon from '@mui/icons-material/Block'
import defaultTexts from '../../../texts/texts'
import { servicesSuspendPolicy, servicesFinalPolicy } from '../_common/billingConstants'
import { checkAccessRequest } from '../../../_network/request'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomSelectField from '../../_common/_form_fields/CustomSelectField'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import CustomDialog from '../../_common/CustomDialog'

const ConfigBillingRegionItem = (props) => {
  const {
    is_default,
    service,
    service_domain,
    api_version,
    api_version_list,
    is_identity_provider,
    default_region,
    region_name,
    app_credential_id,
    resource_suspend_period,
    resource_delete_period,
    resource_suspend_policy,
    resource_delete_policy,
    access,
    setBackendUserData,
    backendIsIdentityProviderSelected,
    billingServicesConfigurable,
    errors,
    setErrors,
    submitted,
    validateFormFields,
    appCredentialSecret,
    setAppCredentialSecret,
  } = props
  const theme = useTheme()
  
  const [serviceDomain, setServiceDomain] = useState('')
  const [apiVersion, setApiVersion] = useState('')
  const [isIdentityProvider, setIsIdentityProvider] = useState(service === 'identity_keystone' ? true : false)
  const [defaultRegion, setDefaultRegion] = useState('')
  const [regionName, setRegionName] = useState('')
  const [appCredentialId, setAppCredentialId] = useState('')
  const [suspendAction, setSuspendAction] = useState('')
  const [suspendDelay, setSuspendDelay] = useState('')
  const [finalAction, setFinalAction] = useState('')
  const [finalActionDelay, setFinalActionDelay] = useState('')
  const [helperText, setHelperText] = useState('')
  const [helperDialogOpen, setHelperDialogOpen] = useState(false)
  const [checkAccessResponse, setCheckAccessResponse] = useState(defaultTexts.addSubscriptionResponsePlaceholder)

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const helpIconStyle = {
    color: theme.palette.oxfordBlue,
    marginTop: '-5px',
  }

  const responseTextStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
    borderRadius: '20px',
    padding: '20px',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontWeight: '500',
  }

  const checkAccessButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const getError = (field_key) => {
    const field_key_name = service + '-' + region_name + '-' + field_key
    const error_object = errors.find(item => item.field_key === field_key_name)
    return error_object ? true : false
  }

  const updateBackendUserData = (field_key, value) => {
    setBackendUserData(prev => {
      let updated_data = []

      prev.forEach(item => {
        if ((!region_name && item.service === service) || (region_name && item.service === service && item.region_name === region_name)) {
          updated_data.push({ ...item, [field_key]: value })
        } else if (field_key === 'default_region' && item.is_default) {
          updated_data.push({ ...item, region_name: value })
        } else {
          updated_data.push(item)
        }
      })

      return updated_data
    })

    validateFormFields()
  }

  const handleHelperDialogOpen = (value) => {
    setHelperDialogOpen(true)
    setHelperText(value)
  }

  const handleHelperDialogClose = () => {
    setHelperDialogOpen(false)
    setHelperText('')
  }

  const handleChangeServiceDomain = (e) => {
    const value = e.target.value
    setServiceDomain(value)
    updateBackendUserData('service_domain', value)
    updateBackendUserData('access', '')
    setCheckAccessResponse(defaultTexts.addSubscriptionResponsePlaceholder)
  }

  const handleChangeApiVersion = (value) => {
    setApiVersion(value)
    updateBackendUserData('api_version', value)
    if (value) {
      setErrors(prev => prev.filter(item => item.field_key !== service + '-' + region_name + '-api_version'))
    }
  }

  const handleChangeIsIdentityProvider = (value) => {
    setIsIdentityProvider(value)
    updateBackendUserData('is_identity_provider', value)
  }

  const handleChangeDefaultRegion = (e) => {
    const value = e.target.value
    setDefaultRegion(value)
    updateBackendUserData('default_region', value ? value : 'Region-1')
  }

  const handleChangeRegionName = (e) => {
    const value = e.target.value
    setRegionName(value)
    updateBackendUserData('region_name', value)
  }

  const handleChangeAppCredentialId = (e) => {
    const value = e.target.value
    setAppCredentialId(value)
    updateBackendUserData('app_credential_id', value)
  }

  const handleChangeAppCredentialSecret = (e) => {
    const value = e.target.value
    setAppCredentialSecret(value)
  }

  const handleChangeSuspendAction = (value) => {
    setSuspendAction(value)
    updateBackendUserData('resource_suspend_policy', value)
    if (value) {
      setErrors(prev => prev.filter(item => item.field_key !== service + '-' + region_name + '-resource_suspend_policy'))
    }
  }

  const handleChangeSuspendDelay = (e) => {
    const value = e.target.value
    setSuspendDelay(value)
    updateBackendUserData('resource_suspend_period', value)
  }

  const handleChangeFinalAction = (value) => {
    setFinalAction(value)
    updateBackendUserData('resource_delete_policy', value)
    if (value) {
      setErrors(prev => prev.filter(item => item.field_key !== service + '-' + region_name + '-resource_delete_policy'))
    }
  }

  const handleChangeFinalActionDelay = (e) => {
    const value = e.target.value
    setFinalActionDelay(value)
    updateBackendUserData('resource_delete_period', value)
  }

  const handleCheckAccess = async () => {
    setCheckAccessResponse('loading...')

    const url = serviceDomain
    const response = await checkAccessRequest(url)    

    if (response && response.data) {
      setCheckAccessResponse(response.data)
    }

    if (response && response.status_code && response.status_code < 400) {
      updateBackendUserData('access', 'checked')
    } else {
      updateBackendUserData('access', 'not_checked')
      setCheckAccessResponse('Access denied.')
    }
  }
  
  useEffect(() => {
    setServiceDomain(service_domain)
  }, [service_domain])

  useEffect(() => {
    setApiVersion(api_version)
  }, [api_version])

  useEffect(() => {
    setIsIdentityProvider(is_identity_provider)
  }, [is_identity_provider])

  useEffect(() => {
    setDefaultRegion(default_region)
  }, [default_region])

  useEffect(() => {
    setRegionName(region_name)
  }, [region_name])

  useEffect(() => {
    setAppCredentialId(app_credential_id)
  }, [app_credential_id])

  useEffect(() => {
    setSuspendAction(resource_suspend_policy)
  }, [resource_suspend_policy])

  useEffect(() => {
    setSuspendDelay(resource_suspend_period)
  }, [resource_suspend_period])

  useEffect(() => {
    setFinalAction(resource_delete_policy)
  }, [resource_delete_policy])

  useEffect(() => {
    setFinalActionDelay(resource_delete_period)
  }, [resource_delete_period])

  return (
    <>
      <Box sx={{ padding: '20px' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 5 }}>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.serviceDomain}
                    currentValue={serviceDomain}
                    setCurrentValue={handleChangeServiceDomain}
                    required={true}
                    error={getError('service_domain') && submitted}
                    disabled={!billingServicesConfigurable}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.serviceDomainHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomSelectField
                    label={defaultTexts.apiVersion}
                    items={api_version_list} 
                    currentValue={apiVersion}
                    setCurrentValue={handleChangeApiVersion}
                    required={true}
                    error={getError('api_version') && submitted}
                    disabled={!billingServicesConfigurable}
                    self_item_titles={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomCheckboxField
                    label={defaultTexts.isIdentityProvider}
                    checked={isIdentityProvider}
                    handleChange={handleChangeIsIdentityProvider}
                    disabled={(backendIsIdentityProviderSelected && !isIdentityProvider) || !billingServicesConfigurable}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.isIdentityProviderHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  {
                    isIdentityProvider ?
                    <CustomTextField 
                      variant='outlined'
                      label={defaultTexts.defaultRegion}
                      currentValue={defaultRegion}
                      setCurrentValue={handleChangeDefaultRegion}
                      required={true}
                      error={getError('default_region') && submitted}
                      disabled={!billingServicesConfigurable}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    /> :
                    <CustomTextField 
                      variant='outlined'
                      label={defaultTexts.regionName}
                      currentValue={regionName}
                      setCurrentValue={handleChangeRegionName}
                      required={true}
                      error={getError('region_name') && submitted}
                      disabled={is_default || !billingServicesConfigurable}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  }
                </Grid>
              </Grid>
            </Box>

            {
              isIdentityProvider &&
              <>
                <Box sx={fieldBoxStyle}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 10 }}>
                      <CustomTextField 
                        variant='outlined'
                        label={defaultTexts.appCredentialId}
                        currentValue={appCredentialId}
                        setCurrentValue={handleChangeAppCredentialId}
                        required={true}
                        error={getError('app_credential_id') && submitted}
                        disabled={!billingServicesConfigurable}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.appCredentialIdHelperText)}>
                        <HelpOutlineIcon fontSize='large' />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={fieldBoxStyle}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 10 }}>
                      <CustomTextField 
                        variant='outlined'
                        label={defaultTexts.appCredentialSecret}
                        currentValue={appCredentialSecret}
                        setCurrentValue={handleChangeAppCredentialSecret}
                        required={true}
                        error={getError('app_credential_secret') && submitted}
                        disabled={!billingServicesConfigurable}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </>
            }

          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            
            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomSelectField
                    label={defaultTexts.suspendAction}
                    items={service ? servicesSuspendPolicy[service] : []} 
                    currentValue={suspendAction}
                    setCurrentValue={handleChangeSuspendAction}
                    required={true}
                    error={getError('resource_suspend_policy') && submitted}
                    item_titles={defaultTexts}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.suspendActionHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomTextField
                    type='number'
                    min={1}
                    variant='outlined'
                    label={defaultTexts.suspendDelayMinutes}
                    currentValue={suspendDelay}
                    setCurrentValue={handleChangeSuspendDelay}
                    required={true}
                    error={getError('resource_suspend_period') && submitted}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomSelectField
                    label={defaultTexts.finalAction}
                    items={service ? servicesFinalPolicy[service] : []} 
                    currentValue={finalAction}
                    setCurrentValue={handleChangeFinalAction}
                    required={true}
                    error={getError('resource_delete_policy') && submitted}
                    item_titles={defaultTexts}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.finalActionHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomTextField
                    type='number'
                    min={1}
                    variant='outlined'
                    label={defaultTexts.finalActionDelayMinutes}
                    currentValue={finalActionDelay}
                    setCurrentValue={handleChangeFinalActionDelay}
                    required={true}
                    error={getError('resource_delete_period') && submitted}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>

          </Grid>
        </Grid>

        {
          billingServicesConfigurable &&
          <>
            <Stack
              direction='row'
              justifyContent='flex-end'
              sx={{ margin: '20px 0px' }}
            >
              {
                access === 'checked' &&
                <Box sx={{ color: theme.palette.success.main, marginRight: '10px' }}>
                  <DoneIcon fontSize='large' />
                </Box>
              }
              {
                access === 'not_checked' &&
                <Box sx={{ color: theme.palette.error.main, marginRight: '10px' }}>
                  <BlockIcon fontSize='large' />
                </Box>
              }
              <Button
                variant='text'
                disableRipple
                disableElevation
                disabled={!serviceDomain ? true : false}
                onClick={handleCheckAccess}
                sx={checkAccessButtonStyle}
              >
                {defaultTexts.checkAccess}
              </Button>
            </Stack>
            <Box sx={responseTextStyle}>
              <pre style={{ whiteSpace: 'pre-wrap' }}>
                { checkAccessResponse ? JSON.stringify(checkAccessResponse, null, 2) : defaultTexts.anErrorOccured }
              </pre>
            </Box>
          </>
        }

      </Box>

      <CustomDialog
        open={helperDialogOpen}
        onClose={handleHelperDialogClose}
        dialogBody={{
          text: helperText,
        }}
      />
    </>
  )
}

export default ConfigBillingRegionItem